import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { logger } from '@app/core/helpers/logger';
import { AccessTokenService } from '@app/core/services/access-token.service';
import { GameService } from '@app/core/services/game.service';
import { EBEvt } from '@app/core/shared/eb-evt';
import { ModalComponent } from '@core/components/modal/modal.component';
import { AccountService, ContextService, LanguageService, ScreenService } from '@core/services';
import { environment } from '@env/environment';
import { DestroyableComponent } from '@models/destroyable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  EContentOrder,
  EContentPaneFormat,
  GameModeEnum,
  GameResults,
  IContentPane,
  LessonSlide,
  User,
} from 'lingo2-models';
import { MetaData, NgEventBus } from 'ng-event-bus';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil, tap } from 'rxjs/operators';

export interface ISelectedTextData {
  selectedWord: string;
  selectedPosition: ISelectedPosition;
}

interface ISelectedPosition {
  top: number;
  left: number;
}

@Component({
  selector: 'app-lesson-game',
  templateUrl: './lesson-game.component.html',
  styleUrls: ['./lesson-game.component.scss'],
})
export class LessonGameComponent extends DestroyableComponent implements OnInit, OnDestroy, AfterViewInit {
  errorNotificationService = inject(ErrorNotificationService);
  private contextService = inject(ContextService);
  private languageService = inject(LanguageService);
  private accessTokenService = inject(AccessTokenService);
  private screenService = inject(ScreenService);
  private gameService = inject(GameService);
  private sanitizer = inject(DomSanitizer);
  private evtbus = inject(NgEventBus);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  deviceService = inject(DeviceDetectorService);

  @ViewChild('modal', { read: false }) modal: ModalComponent;
  @HostListener('window:message', ['$event'])
  onMessage(event) {
    this.receiveMessage(event);
  }
  @Input() gameMode: GameModeEnum;
  @Input() public set lessonId(lessonId: string) {
    if (!lessonId.length) {
      return;
    }
    this._lessonId = lessonId;
    this.generateGameLink();
  }
  public get lessonId() {
    return this._lessonId;
  }

  @Input() public set replayGameId(replayGameId: string) {
    if (!replayGameId) {
      return;
    }
    this._replayGameId = replayGameId;
    this.generateGameLink();
  }
  public get replayGameId() {
    return this._replayGameId;
  }

  public get gameState(): any {
    return this._gameState;
  }
  @Input() author: User;
  @Input() withoutHeader: boolean;

  @Output() submitted = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<boolean>();
  @Output() started = new EventEmitter<boolean>();
  @Output() loaded = new EventEmitter<boolean>();
  @Output() finished = new EventEmitter<boolean>();
  public selectedText: ISelectedTextData;
  public state = {
    waiting: true,
    ready: false,
    started: false,
    in_progress: false,
    finished: false,
    showAbuse: false,
  };

  public safeGameUrl: SafeResourceUrl;

  public gameResults: Partial<GameResults>;
  public abuseSlide: LessonSlide;

  public isShowFinishScreen = false;
  public answerState: string;
  public isSmallScreen = true;
  public isMobileHeaderOpened: any = false;
  public lessonPane: IContentPane;

  public frameHeight = '350px';
  public currentLanguage = 'en';
  private myId: string = '';
  public gameIsFinish = false;
  public gameIsFinished = false;
  public frameLoaded = false;

  private _lessonId: string;
  private _replayGameId: string;
  private _started_at = new Date();
  private _gameState: any;
  private _gameResults: any;

  ngOnInit() {
    this.init_eb_events();
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.isSmallScreen = this.screenService.isSmallScreen;
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });

    this.contextService.me$
      .pipe(
        tap((me) => {
          this.myId = me.id;
          if (!this.safeGameUrl) {
            this.generateGameLink();
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.lessonPane = {
      order: 1,
      format: EContentPaneFormat['new-exercises'],
      loaded: false,
      title: 'library.titles.lessons-from-teacher',
      new_grid_view: 'vertical-square',
      default_element_width: 200,
      contentFilter: {
        is_library: true,
        author_id: this.author.id,
        order: EContentOrder.viewed_desc,
        limit: 20,
      },
      rel: 'same-author',
    };

    this.languageService.language$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (language) => {
        this.currentLanguage = language.code;
        this.generateGameLink();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private init_eb_events() {
    this.evtbus
      .on(EBEvt.G_AUTH_TOKEN_ACCESS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((meta: MetaData) => {
        this.sendRefreshTokenMsg(meta.data);
      });
  }

  private sendRefreshTokenMsg(message: string) {
    window.parent.postMessage(`gamelib::token::${message}`, '*');
  }

  public onIsAnswerChecked(event) {
    this.answerState = event;
  }

  public get isLesson(): boolean {
    return !this.isVocabulary;
  }

  public get isVocabulary(): boolean {
    return [GameModeEnum.memorizer, GameModeEnum.scenario, GameModeEnum.test].includes(this.gameMode);
  }

  private prepare() {
    this.state.waiting = true;
    this.state.ready = false;
    this.state.started = false;
    this.start();
  }

  private start() {
    this.state.waiting = false;
    this.state.ready = true;
    this.gameService.clearGameFrame();
  }

  public onStateChanged = (state: Partial<any>) => {
    // logger.log('LessonGameComponent:onStateChanged', state);
    if (state.isFinish !== undefined) {
      this.isShowFinishScreen = !!state.isFinish;
    }
  };

  public showAbuse = (slide: LessonSlide) => {
    logger.log('LessonGameComponent:showAbuse', slide);
    this.state.showAbuse = true;
    this.abuseSlide = slide;
  };

  public onClose() {
    logger.log('LessonGameComponent:onClose');
    this.closed.emit(true);
  }

  public closeAbuse(event) {
    logger.log(event);
  }

  public onStart() {
    this.started.emit(true);
  }

  public showMobileHeader(): void {
    this.isMobileHeaderOpened = {};
  }

  public onTextSelect(e: ISelectedTextData) {
    this.selectedText = e;
  }

  public generateGameLink() {
    if (!this.myId.length || !this.lessonId.length) {
      return;
    }
    let url = '';
    const hostUrl = environment.gamelib_app_host;
    const frameUrl = `${hostUrl}/game`;
    const token = this.accessTokenService.getAccessToken();
    const lessonId = this.lessonId;
    const gameId = this.replayGameId;
    const host = environment.gamelib_app_api_host.includes('local') ? 'onclass.tech' : environment.gamelib_app_api_host;
    const mode = this.gameMode;
    const lang = this.currentLanguage || 'en';
    const myId = this.myId;
    url = `${frameUrl}?token=${token}&lessonId=${lessonId}&userId=${myId}&host=${host}&mode=${mode}&lang=${lang}`;

    if (this.replayGameId) {
      url = `${url}&gameId=${gameId}`;
    }

    this.safeGameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.prepare();
  }

  public receiveMessage(event: MessageEvent) {
    const message = event?.data;
    if (typeof message === 'string' || message instanceof String) {
      if (message.includes('gamelib')) {
        const messageArray = message.split('::');
        const lessonId = messageArray[1];
        if (this.lessonId !== lessonId) {
          return;
        }

        const action = messageArray[2];
        const value = messageArray[3];
        if (action && action === 'resize') {
          this.frameResize(value);
        } else if (action && action === 'closed') {
          this.onClose();
        } else if (action && action === 'review') {
          this.submitted.emit(true);
        } else if (action && action === 'textselected') {
          this.onTextSelect(JSON.parse(value));
        } else if (action && action === 'answerstate') {
          this.onIsAnswerChecked(value);
        } else if (action && action === 'isFinish') {
          this.gameIsFinish = value === 'true';
        } else if (action && action === 'finished') {
          this.gameIsFinished = value === 'true';
        } else if (action && action === 'loaded') {
          this.onFrameLoaded();
        } else if (action && action === 'openAuthorPage') {
          window.open(AccountService.accountRoute(this.author).join('/'), '_blank');
        }
      }
    }
  }

  public frameResize(heigth) {
    this.frameHeight = `${heigth}px`;
    this.cdr.detectChanges();
  }

  public onFrameLoaded() {
    this.frameLoaded = true;
    this.gameService.setGameFrame(document.getElementById('gameFrame'));
  }

  public onScroll(event) {
    console.log('IFrame scrolled', event);
  }
}
